/**
 * Custom Events Tracking — product, cart and PDP events.
 *
 * Companion to assets/analytics.js, which owns the dataLayer push helpers and
 * every site-chrome event. Split in two so neither file approaches the 500-line
 * ceiling; both are global utilities loaded once from layout/theme.liquid.
 *
 * Covers: quickview_open · dl_view_item_list · bundle_selection_* ·
 * notifyme_* · reviews_write_* · clickandcollect_*
 *
 * subscription_* lives in assets/analytics-subscription.js — it is the only one
 * that has to reach into a third-party shadow root.
 */
(function () {
  'use strict';

  var W = window;
  /* Refuse a second execution of this file. The `inited` guard below is
     module-scoped, so it cannot see a duplicate <script> include — and every
     hook here would then be registered twice, silently doubling every event.
     Mirrors the dataset.*Ready guards the theme's own controllers use. */
  if (W.__whittardAnalyticsProduct) return;
  W.__whittardAnalyticsProduct = true;
  var A = W.WhittardAnalytics;
  if (!A) return;
  var push = A.push;
  var pushRaw = A.pushRaw;
  /* Market-neutral path for the `list` field — see normalisePath in assets/analytics.js. */
  var currentList = A.currentList;
  /* One body observer serves all three files — see assets/analytics.js. */
  var onDomChange = A.onDomChange;

  var body = document.body;
  var CURRENCY = (body && body.getAttribute('data-currency')) || 'GBP';
  var BRAND = (body && body.getAttribute('data-shop-name')) || '';

  /* ═══════════════════════════════════════════════
     Product impressions

     Each product card carries its own impression payload as data-track-item
     (see snippets/product-card.liquid for the Liquid-rendered cards and
     snippets/collection-tagalys-card.liquid + mini-bag-recommendations-script
     for the JS-rendered ones). position, list, brand and currency are added
     here so no renderer has to know about them.
  ═══════════════════════════════════════════════ */

  function itemFrom(card, position, list) {
    if (!card) return null;
    var raw = card.getAttribute('data-track-item');
    if (!raw) return null;
    var data;
    try { data = JSON.parse(raw); } catch (e) { return null; }
    return {
      id: data.id || data.product_id || '',
      name: data.name || '',
      brand: data.brand || BRAND,
      category: data.category || '',
      variant: data.variant || '',
      price: data.price || '0.00',
      position: position,
      list: list,
      product_id: data.product_id || '',
      variant_id: data.variant_id || '',
      compare_at_price: data.compare_at_price || '0.00',
      image: data.image || ''
    };
  }

  /* ═══════════════════════════════════════════════
     Quick View — quickview_open

     The ecommerce object only — no quickview_open_properties node.

     The spec's two halves read differently: its parameter table names a single
     `product_id`, while its code example shows the full ecommerce impressions
     object and its development note says the shape mirrors Elevar's native
     dl_view_item_list. This carried both for a while rather than guess which the
     GA configuration would read; QA confirmed the ecommerce object is the whole
     contract (2026-08-31), and `product_id` is already inside the impression, so
     the extra node was duplicating a value nothing consumed.
  ═══════════════════════════════════════════════ */

  function watchQuickView() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-quick-view-open]');
      if (!trigger) return;
      var card = trigger.closest('[data-product-card]');
      var item = itemFrom(card, 1, currentList());
      /* Without a payload there is no product to report. Falling back to the
         handle alone would push an impression GA cannot join to anything, so
         the event is skipped rather than sent half-populated. */
      if (!item) return;
      pushRaw({
        event: 'quickview_open',
        ecommerce: { currencyCode: CURRENCY, impressions: [item] }
      });
    }, true);
  }

  /* ═══════════════════════════════════════════════
     Product lists — dl_view_item_list
     Fires when a marked list first comes into view. Re-fires only if the list's
     contents actually change (the mini bag re-renders its upsells on every cart
     mutation, and a repeated identical push would inflate the impression count).
  ═══════════════════════════════════════════════ */

  var CARD_SELECTOR = '[data-track-item]';

  function signature(cards) {
    return cards.map(function (c) { return c.getAttribute('data-track-item'); }).join('|');
  }

  function reportList(container) {
    var cards = Array.prototype.slice.call(container.querySelectorAll(CARD_SELECTOR));
    if (!cards.length) return;
    var sig = signature(cards);
    if (container.__trackedListSignature === sig) return;
    container.__trackedListSignature = sig;

    var list = container.getAttribute('data-track-item-list') || currentList();
    var impressions = cards
      .map(function (card, i) { return itemFrom(card, i + 1, list); })
      .filter(Boolean);
    if (!impressions.length) return;

    pushRaw({
      event: 'dl_view_item_list',
      ecommerce: { currencyCode: CURRENCY, impressions: impressions }
    });
  }

  function watchProductLists() {
    if (!W.IntersectionObserver) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) reportList(entry.target);
      });
    }, { threshold: 0.1 });

    var observed = new WeakSet();

    /* Every list here is populated asynchronously — Tagalys fills the carousels,
       the mini bag re-renders its panels — so the cards almost never exist when
       the container is first observed. Re-reading on change is what makes the
       impression fire against real content rather than an empty container.

       Three passes, in this order, because the single-pass version was a forced
       synchronous layout: it interleaved io.observe() and reportList() — both of
       which can invalidate layout — with getBoundingClientRect() reads, so the
       browser recomputed geometry once per element rather than once per callback.
       Measured at 656 ms on the homepage and 491 ms on the PDP (Vervaunt UAT,
       15 Sept 2026, item 2), and separately by Lighthouse's forced-reflow insight.

       Pass 1 registers observers and nothing else. Pass 2 reads every rect in one
       uninterrupted run, so the browser can satisfy them all from one layout.
       Pass 3 does the reporting, after the last read.

       Which elements get read is deliberately unchanged: EVERY matched element is
       measured, including one observed for the first time in pass 1. Restricting
       pass 2 to already-observed elements would look like a free optimisation but
       is not — the observer's threshold is 0.1, while this rect test fires on any
       sliver of visibility, so a container 5% in view would silently stop
       reporting an impression it reports today. The reflow fix is the batching,
       not a narrower set of elements. */
    onDomChange(function () {
      var els = document.querySelectorAll('[data-track-item-list]');
      if (!els.length) return;

      // Pass 1 — writes only.
      var i;
      for (i = 0; i < els.length; i++) {
        if (!observed.has(els[i])) {
          observed.add(els[i]);
          io.observe(els[i]);
        }
      }

      // Pass 2 — reads only. viewportHeight is hoisted out of the loop for the
      // same reason: innerHeight is itself a layout read.
      var viewportHeight = W.innerHeight;
      var visible = [];
      for (i = 0; i < els.length; i++) {
        var rect = els[i].getBoundingClientRect();
        if (rect.width > 0 && rect.top < viewportHeight && rect.bottom > 0) {
          visible.push(els[i]);
        }
      }

      // Pass 3 — act, once every read is done.
      for (i = 0; i < visible.length; i++) reportList(visible[i]);
    });
  }

  /* ═══════════════════════════════════════════════
     Bundles — bundle_selection_choose / _add / _remove

     Add and remove are read from the engine's own custom-bundle:changed state
     rather than from click targets: a slot can be filled from the picker, from
     the quick view, or restored from a template, and diffing the state catches
     every path with one hook.
  ═══════════════════════════════════════════════ */

  function watchBundle() {
    /* A fixed slot's Choose button is inert — product-custom-bundle-ui.js skips
       preparePicker for it, so no picker opens and there is nothing for the
       shopper to choose. Reporting those clicks would inflate the event with
       interactions that never happened. */
    function isFixed(slotEl) {
      return !slotEl || slotEl.getAttribute('data-cb-fixed') === 'true';
    }

    /* Only the Choose button is hooked, deliberately. The + tile is a second,
       larger target for the same action, but product-custom-bundle-ui.js handles
       it by calling tileChoose.click() — so a hook on the tile reported once for
       the tile and again for the forwarded click. One hook on the button catches
       both routes exactly once (QA, Nahuel, 2026-08-28). */
    document.addEventListener('click', function (e) {
      var choose = e.target.closest('[data-cb-choose]');
      if (!choose) return;
      if (isFixed(choose.closest('[data-cb-slot]'))) return;
      push('bundle_selection_choose', { slot: parseInt(choose.getAttribute('data-cb-choose'), 10) });
    }, true);

    /* Seeded from the engine's live state rather than from the first event.

       product-custom-bundle.js emits its initial state at the end of its own
       deferred script, which runs before DOMContentLoaded and therefore before
       this listener exists. Waiting for a first event to use as the baseline
       meant the baseline became the shopper's FIRST selection, and that
       selection was never reported. Reading the state directly gets the true
       starting point, so pre-filled fixed slots still do not report as customer
       selections and the first real add does. */
    var previous = null;
    var engine = W.WhittardCustomBundle;
    if (engine && typeof engine.getState === 'function') {
      try {
        previous = (engine.getState().selections || []).slice();
      } catch (e) {
        previous = null;
      }
    }

    document.addEventListener('custom-bundle:changed', function (e) {
      var selections = (e.detail && e.detail.selections) || [];
      /* Fallback for the case where the engine is not exposed yet: seed from the
         first event, as before. */
      if (previous === null) {
        previous = selections.slice();
        return;
      }

      selections.forEach(function (current, i) {
        var before = previous[i] || null;
        var slot = i + 1;
        if (!current && before) {
          push('bundle_selection_remove', { slot: slot });
          return;
        }
        if (!current) return;
        if (before && String(before.productId) === String(current.productId)) return;
        push('bundle_selection_add', {
          slot: current.slot || slot,
          product_name: current.title || '',
          product_id: String(current.productId || '')
        });
      });

      previous = selections.slice();
    });
  }

  /* ═══════════════════════════════════════════════
     Shared helper — report when a hidden success panel is revealed.
     Both the notify-me and review forms confirm success by unhiding a panel
     rather than navigating, so the class change is the completion signal.
  ═══════════════════════════════════════════════ */

  function onRevealed(selector, handler) {
    function bind(el) {
      if (el.__revealWatched) return;
      el.__revealWatched = true;
      var wasHidden = el.classList.contains('hidden');
      /* Scoped to the panel itself and to the one attribute that matters, so
         this observer never sees an unrelated mutation. */
      new MutationObserver(function () {
        var isHidden = el.classList.contains('hidden');
        if (isHidden === wasHidden) return;
        wasHidden = isHidden;
        if (!isHidden) handler(el);
      }).observe(el, { attributes: true, attributeFilter: ['class'] });
    }
    onDomChange(function () {
      document.querySelectorAll(selector).forEach(bind);
    });
  }

  /* ═══════════════════════════════════════════════
     Back in Stock — notifyme_open, notifyme_submit
  ═══════════════════════════════════════════════ */

  function watchNotifyMe() {
    document.addEventListener('click', function (e) {
      /* The PDP buy box, the variant picker's out-of-stock rows and the quick
         view each have their own trigger; all three open the same form. */
      if (e.target.closest('[data-notify-me-open], [data-quick-view-notify-trigger]')) {
        push('notifyme_open');
      }
    }, true);

    /* Two success panels, because the quick view runs its own notify-me flow with
       its own markup — a submit from a product card reported nothing while only
       the PDP drawer's panel was watched. */
    onRevealed('[data-notify-success], [data-quick-view-notify-success]', function () {
      push('notifyme_submit');
    });
  }

  /* ═══════════════════════════════════════════════
     Reviews (Yotpo) — reviews_write_open, reviews_write_submit
  ═══════════════════════════════════════════════ */

  function watchReviews() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-drawer-open="review-form-drawer"]')) push('reviews_write_open');
    }, true);

    onRevealed('[data-form-success]', function () { push('reviews_write_submit'); });
  }

  /* ═══════════════════════════════════════════════
     Find & Collect in Store — clickandcollect_*
  ═══════════════════════════════════════════════ */

  function watchClickAndCollect() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-find-in-store-open]')) {
        push('clickandcollect_open');
        return;
      }
      var item = e.target.closest('[data-find-store-item]');
      if (!item) return;
      var name = item.querySelector('[data-find-store-name]');
      push('clickandcollect_select', { store_name: name ? name.textContent.trim() : '' });
    }, true);

    document.addEventListener('submit', function (e) {
      if (!e.target.closest('[data-find-store-form]')) return;
      /* The submit button doubles as "View store details" once a store is
         picked, and that path navigates away rather than searching. */
      var selected = document.querySelector('[data-find-store-item][data-selected="true"]');
      if (selected) return;
      push('clickandcollect_search');
    }, true);

    document.addEventListener('drawer:close', function (e) {
      var id = e.detail && e.detail.id;
      if (id && id.indexOf('FindInStoreDrawer') === 0) push('clickandcollect_close');
    });
  }

  /* ═══════════════════════════════════════════════
     Init
  ═══════════════════════════════════════════════ */

  /* Isolated per watcher for the same reason as assets/analytics.js's init —
     one throw must not take the rest of the tracking down with it. */
  function safe(name, fn) {
    try {
      fn();
    } catch (e) {
      if (W.console) W.console.warn('[analytics-product] ' + name + ' failed to initialise', e);
    }
  }

  /* Idempotent for the same reason as assets/analytics.js's init — see the note
     there on double-counted events. */
  var inited = false;

  function init() {
    if (inited) return;
    inited = true;
    safe('quickView', watchQuickView);
    safe('productLists', watchProductLists);
    safe('bundle', watchBundle);
    safe('notifyMe', watchNotifyMe);
    safe('reviews', watchReviews);
    safe('clickAndCollect', watchClickAndCollect);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
